import { Button, Form, ForwardedInput, Label } from 'hatch-components';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useConnect } from 'redux-bundler-hook';
import DrawerBody from 'root/components/molecules/Drawer/components/DrawerBody';
import DrawerError from 'root/components/molecules/Drawer/components/DrawerError';
import DrawerFooter from 'root/components/molecules/Drawer/components/DrawerFooter';
import { FormSelect } from 'root/components/molecules/FormSelect';
import validateEmail from 'utils/validateEmail';

const ROLES = [
  { label: 'Manager', value: 'manager' },
  { label: 'User', value: 'user' },
];

const CreateWorkspaceForm = ({ onClose, form }) => {
  const {
    boardOptions,
    currentOrganizationId,
    isInvitingTeamMember,
    doInviteTeamMember,
    doCreateNotificationMessage,
  } = useConnect(
    'selectBoardOptions',
    'selectCurrentOrganizationId',
    'selectIsInvitingTeamMember',
    'doInviteTeamMember',
    'doCreateNotificationMessage',
  );

  const { register, control, handleSubmit, errors, watch } = form;

  const [submitErrorMessage, setSubmitErrorMessage] = useState('');

  const done = (error, { data } = {}) => {
    if (error) {
      // TODO set errors in form
      return setSubmitErrorMessage(error?.message);
    }

    doCreateNotificationMessage({
      message: `An email invitation has been sent to ${data.email}.`,
      type: 'success',
    });
    return onClose({ ignore: true });
  };

  const onSubmit = (data) => {
    if (isInvitingTeamMember) return;

    const { email, assignedBoards, role, firstName, lastName } = data;
    doInviteTeamMember(
      {
        data: {
          assignedBoardIDs: assignedBoards.map(({ value }) => value),
          email,
          firstName,
          lastName,
          role: role.value,
        },
        organizationId: currentOrganizationId,
      },
      done,
    );
  };

  const role = watch('role');
  const roleHelperText =
    (role?.value || '').toLowerCase() === 'user'
      ? 'Users only have access to their personal settings, they cannot update workspaces or other members'
      : 'Managers can add or edit members and workspaces within the Account';

  return (
    <>
      {submitErrorMessage ? <DrawerError message={submitErrorMessage} /> : null}
      <DrawerBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Label error={!!errors?.email} htmlFor="firstName" required>
            First Name
          </Label>
          <ForwardedInput
            data-test-id="92e9c3e4-e7ad-4160-8bba-c87087bd4ffb"
            error={!!errors?.firstName}
            fullWidth
            helperText={errors?.firstName?.message}
            id="firstName"
            name="firstName"
            ref={register({ required: 'A first name is required' })}
          />
          <Label error={!!errors?.lastName} htmlFor="lastName" required>
            Last Name
          </Label>
          <ForwardedInput
            data-test-id="815b0c1e-b040-4e1a-b5bd-e991bd53b2d0"
            error={!!errors?.lastName}
            fullWidth
            helperText={errors?.lastName?.message}
            id="lastName"
            name="lastName"
            ref={register({ required: 'A last name is required' })}
          />
          <Label error={!!errors?.email} htmlFor="email" required>
            Email
          </Label>
          <ForwardedInput
            data-test-id="544460e6-53f4-4e5a-aaad-ada83f48e2cd"
            error={!!errors?.email}
            fullWidth
            helperText={errors?.email?.message}
            id="email"
            name="email"
            ref={register({
              required: 'An email address is required',
              validate: (v) =>
                validateEmail(v) || 'Please enter a valid email address.',
            })}
          />
          <Label error={!!errors?.role} htmlFor="role" required>
            Role
          </Label>
          <FormSelect
            control={control}
            data-test-id="1dc33292-d266-4bc8-bfc9-1ca2cbb155e6"
            error={!!errors?.role}
            fullWidth
            helperText={errors?.role?.message || roleHelperText}
            id="role"
            name="role"
            options={ROLES}
            rules={{ required: 'A team member must have a role' }}
          />
          <Label error={!!errors?.assignedBoards} htmlFor="assignedBoards">
            Add to Workspaces:
          </Label>
          <FormSelect
            control={control}
            data-test-id="a55d505f-150d-4b1e-b7bb-353e93a9b41f"
            error={!!errors?.assignedBoards}
            fullWidth
            helperText={errors?.assignedBoards?.message}
            id="assignedBoards"
            isMulti
            name="assignedBoards"
            menuPlacement="top"
            options={boardOptions}
          />
        </Form>
      </DrawerBody>
      <DrawerFooter>
        <Button
          data-test-id="a76525f3-f211-4837-b4b0-cdff4bb5a4bf"
          fetching={isInvitingTeamMember}
          minWidth={90}
          onClick={handleSubmit(onSubmit)}
          size="sm"
          variant="primary"
        >
          Send Invite
        </Button>
      </DrawerFooter>
    </>
  );
};

CreateWorkspaceForm.propTypes = {
  form: PropTypes.any, // eslint-disable-line
  onClose: PropTypes.func.isRequired,
};

CreateWorkspaceForm.defaultProps = {
  form: {},
};

export default CreateWorkspaceForm;
